import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { Container, Typography, Box, Button } from "@mui/material";
import Footer from "./components/Footer";
import ThemeProvider from "./context/ThemeContext";
import { ProfileImage, PageContainer } from "./components/styled/StyledComponents";
import useImages from "./hooks/useImages";
import AlacatLogo from "./assets/Alacat_01_Artboard_1.png";

const About = lazy(() => import("./pages/About"));
const Resume = lazy(() => import("./pages/Resume"));
const Baking = lazy(() => import("./pages/Baking"));
const Arts = lazy(() => import("./pages/Arts"));
const ArtsCollection = lazy(() => import("./pages/ArtsCollection"));
const ArtsDesign = lazy(() => import("./pages/ArtsDesign"));
const Travel = lazy(() => import("./pages/Travel"));
const Contact = lazy(() => import("./pages/Contact"));
const Admin = lazy(() => import("./pages/Admin"));

const menuItems = [
  ["About", "/about"],
  ["Baking", "/baking"],
  ["Arts", "/arts"],
  ["Travel", "/travel"],
];

const Menu = React.memo(() => {
  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 0.5 }}
    >
      {menuItems.map(([label, to]) => (
        <Button
          key={to}
          component={Link}
          to={to}
          sx={{ color: "text.secondary", minWidth: "auto", px: 1, "&:hover": { color: "text.primary", backgroundColor: "transparent" } }}
        >
          {label}
        </Button>
      ))}
    </Box>
  );
});

const SiteHeader = () => (
  <Container maxWidth="lg">
    <Box
      component="header"
      sx={{ maxWidth: 1040, mx: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", borderBottom: 1, borderColor: "divider", py: 1.5 }}
    >
      <Box
        component={Link}
        to="/"
        aria-label="Alacat home"
        sx={{ display: "flex", alignItems: "center", gap: 1.5, textDecoration: "none", color: "inherit" }}
      >
        <Box
          component="img"
          src={AlacatLogo}
          alt="Alacat wordmark and cat illustration"
          sx={{ width: { xs: 70, sm: 88 }, height: { xs: 70, sm: 88 }, objectFit: "contain" }}
        />
        <Typography variant="body2" sx={{ color: "text.secondary", fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontSize: { xs: "1rem", sm: "1.2rem" } }}>
          Food from and for the soul
        </Typography>
      </Box>
      <Menu />
    </Box>
  </Container>
);


const Home = () => {
  const { getProfileImage, isLoaded } = useImages();
  if (!isLoaded) {
    return (
      <Container maxWidth="lg">
        <PageContainer sx={{ textAlign: "center", py: { xs: 8, md: 12 } }}>
          <Typography variant="h6">Loading...</Typography>
        </PageContainer>
      </Container>
    );
  }
  
  const profileImageData = getProfileImage();
  
  return (
    <Container maxWidth="lg">
      <PageContainer sx={{ py: { xs: 4, md: 7 } }}>
        <Box sx={{ maxWidth: 1040, mx: "auto" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 0.85fr) minmax(0, 1fr)" },
              alignItems: "center",
              gap: { xs: 4, md: 8 },
            }}
          >
            <ProfileImage
              component="img"
              src={profileImageData?.src}
              alt={profileImageData?.alt || "Cătălina Ionescu"}
              sx={{ width: { xs: 180, sm: 220, md: 300 }, height: { xs: 220, sm: 270, md: 360 }, mx: { xs: "auto", md: 0 } }}
            />
            <Box>
              <Typography variant="h6" sx={{ color: "secondary.main", fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontWeight: 400, mb: 1 }}>
                A little corner of my world
              </Typography>
              <Typography component="h1" variant="h1" sx={{ fontSize: { xs: "3.2rem", md: "5.2rem" }, lineHeight: 0.95, mb: 3 }}>
                Cătălina<br />Ionescu
              </Typography>
              <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.08em", display: "block", mb: 2 }}>
                Software Engineer · Creative Soul · Explorer
              </Typography>
              <Typography variant="body1" sx={{ maxWidth: 560, mb: 2 }}>
                As a Software Engineer in New York City, I enjoy solving novel problems and mentoring people.
              </Typography>
              <Typography variant="body1" sx={{ maxWidth: 560 }}>
                Outside work, I’m drawn to art, food, and culture. This is a place to share the things I make, the meals I love, and the places that stay with me.
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 3 }}>
                Welcome. I’m glad you’re here.
              </Typography>
            </Box>
          </Box>
        </Box>
      </PageContainer>
    </Container>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <Box sx={{ minHeight: "100vh" }}>
      <Router>
        <Box>
          <SiteHeader />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<Suspense fallback={<div>Loading...</div>}><About /></Suspense>} />
            <Route path="/resume" element={<Suspense fallback={<div>Loading...</div>}><Resume /></Suspense>} />
            <Route path="/baking" element={<Suspense fallback={<div>Loading...</div>}><Baking /></Suspense>} />
            <Route path="/arts" element={<Suspense fallback={<div>Loading...</div>}><Arts /></Suspense>} />
            <Route path="/arts/collection/:collectionSlug" element={<Suspense fallback={<div>Loading...</div>}><ArtsCollection /></Suspense>} />
            <Route path="/arts/design/:id" element={<Suspense fallback={<div>Loading...</div>}><ArtsDesign /></Suspense>} />
            <Route path="/contact" element={<Suspense fallback={<div>Loading...</div>}><Contact /></Suspense>} />
            <Route path="/travel" element={<Suspense fallback={<div>Loading...</div>}><Travel /></Suspense>} />
            <Route path="/admin" element={<Suspense fallback={<div>Loading...</div>}><Admin /></Suspense>} />
            <Route path="*" element={<Home />} />
          </Routes>
          <Footer />
        </Box>
      </Router>
      </Box>
    </ThemeProvider>
  );
}

export default App;

